/**
 * Measurement Finder - Product SKU Auto-fill Script
 * 
 * This script automatically fills product SKU fields based on measurement finder results.
 * All configuration comes from measurement-finder.js via localStorage.
 * No duplicate configuration needed!
 */

(function () {
    'use strict';

    // Only product-specific settings
    const PRODUCT_TYPE_CONFIG = {
        'shades': {
            waitForSelector: 'select[name="Width (Whole Inches)"]',
            // Measurement fields specific to shades products
            measurementFields: [
                { name: 'Width (Whole Inches)', valueKey: 'widthWhole', suffix: '"', type: 'select' },
                { name: 'Width (Fractional Inches)', valueKey: 'widthFraction', suffix: '"', type: 'select' },
                { name: 'Height (Whole Inches)', valueKey: 'heightWhole', suffix: '"', type: 'select' },
                { name: 'Height (Fractional Inches)', valueKey: 'heightFraction', suffix: '"', type: 'select' }
            ]
        },
        'drapery': {
            waitForSelector: 'select[name*="Width"]',
            // Measurement fields specific to drapery products
            // (Adjust field names here if they're different for drapery)
            measurementFields: [
                { name: 'Width', valueKey: 'widthWhole', suffix: '"', type: 'select' },
                { name: 'Height', valueKey: 'heightWhole', suffix: '"', type: 'select' },
            ]
        }
    };

    // Utility functions
    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function waitElement(selector, parentEl) {
        return new Promise(resolve => {
            if (parentEl.querySelector(selector)) {
                return resolve();
            }

            const observer = new MutationObserver(() => {
                if (parentEl.querySelector(selector)) {
                    observer.disconnect();
                    resolve();
                }
            });

            observer.observe(parentEl, {
                childList: true,
                subtree: true
            });
        });
    }

    // Generic field filling function based on field type
    async function fillField(mainContent, field) {
        const { name, value, type } = field;

        switch (type) {
            case 'select':
                await fillSelectField(mainContent, name, value);
                break;
            case 'radio':
                await fillRadioField(mainContent, name, value);
                break;
            case 'text':
                await fillTextField(mainContent, name, value);
                break;
            default:
                console.warn(`Unknown field type: ${type} for field: ${name}`);
        }
    }

    async function fillSelectField(mainContent, name, value) {
        const selector = `select[name*="${name}"]`;
        const element = mainContent.querySelector(selector);

        if (!element) {
            console.warn(`Select element not found: ${selector}`);
            return;
        }

        const values = Array.from(element.options).map(o => o.value);

        // Attempt 1: Exact match (with unit, e.g., 72")
        if (values.includes(value)) {
            element.value = value;
            element.dispatchEvent(new Event('change'));
            console.log(`✓ Filled ${name}: ${value}`);
            return;
        }

        // Attempt 2: Match after removing unit (without unit, e.g., 72)
        // Remove common unit symbols: " ' inch inches
        const valueWithoutUnit = value.toString().replace(/["']/g, '').replace(/\s*(inch|inches)\s*$/i, '').trim();

        if (values.includes(valueWithoutUnit)) {
            element.value = valueWithoutUnit;
            element.dispatchEvent(new Event('change'));
            console.log(`✓ Filled ${name}: ${valueWithoutUnit} (matched without unit)`);
            return;
        }

        // Attempt 3: Normalized match - normalize both input and option values
        const normalizedValue = value.toString().replace(/["']/g, '').replace(/\s*(inch|inches)\s*$/i, '').trim();
        const matchedOption = Array.from(element.options).find(option => {
            const optionValue = option.value.toString().replace(/["']/g, '').replace(/\s*(inch|inches)\s*$/i, '').trim();
            return optionValue === normalizedValue;
        });

        if (matchedOption) {
            element.value = matchedOption.value;
            element.dispatchEvent(new Event('change'));
            console.log(`✓ Filled ${name}: ${matchedOption.value} (matched after normalization)`);
            return;
        }

        console.warn(`Value "${value}" not found in select options for ${name}. Available options:`, values);
    }

    async function fillRadioField(mainContent, name, value) {
        const selector = `input[name*="${name}"][value="${value}"]`;
        const element = mainContent.querySelector(selector);

        if (!element) {
            console.warn(`Radio element not found: ${selector}`);
            return;
        }

        // Only click if not already checked
        if (!element.checked) {
            element.parentElement.click();
            await sleep(200);
            console.log(`✓ Filled ${name}: ${value}`);
        } else {
            console.log(`⊙ ${name} already set to: ${value}`);
        }
    }

    async function fillTextField(mainContent, name, value) {
        const selector = `input[name*="${name}"]`;
        const element = mainContent.querySelector(selector);

        if (!element) {
            console.warn(`Text input element not found: ${selector}`);
            return;
        }

        element.value = value;
        element.dispatchEvent(new Event('input'));
        console.log(`✓ Filled ${name}: ${value}`);
    }

    // Main auto-fill function
    async function autoFillProductSKU() {
        const resultKey = 'measurement-finder-result';
        const mainContent = document.querySelector('#MainContent');

        if (!mainContent) {
            console.warn('MainContent element not found');
            return;
        }

        try {
            // Get stored result
            const result = JSON.parse(localStorage.getItem(resultKey));
            if (!result || typeof result !== 'object') {
                return;
            }

            const productType = result.productType || 'drapery';
            console.log(`🔍 Auto-filling SKU for product type: ${productType}`);

            // Get configuration for this product type
            const config = PRODUCT_TYPE_CONFIG[productType];
            if (!config) {
                console.error(`No configuration found for product type: ${productType}`);
                return;
            }

            // Wait for the required element to appear
            await waitElement(config.waitForSelector, mainContent);
            await sleep(200);

            // Build complete field list from multiple sources
            const fieldsToFill = [];

            // 1. Add fields from autoFillFields (from option type config in measurement-finder.js)
            //    These already include type information!
            if (result.autoFillFields && Array.isArray(result.autoFillFields)) {
                fieldsToFill.push(...result.autoFillFields);
            }

            // 2. Add measurement fields based on product type configuration
            if (config.measurementFields && Array.isArray(config.measurementFields)) {
                for (const fieldDef of config.measurementFields) {
                    const value = result[fieldDef.valueKey];
                    if (value !== undefined && value !== null) {
                        fieldsToFill.push({
                            name: fieldDef.name,
                            value: value + (fieldDef.suffix || ''),
                            type: fieldDef.type
                        });
                    }
                }
            }

            // Fill each field - field object already contains all needed info (name, value, type)
            for (const field of fieldsToFill) {
                try {
                    if (!field.type) {
                        console.warn(`Field missing type information: ${field.name}`);
                        continue;
                    }
                    await fillField(mainContent, field);
                } catch (error) {
                    console.error(`Error filling field ${field.name}:`, error);
                }
            }

            console.log('✅ SKU auto-fill completed successfully');
        } catch (error) {
            console.error('Error in measurement-finder-product-sku-fill.js:', error);
        } finally {
            // Clean up localStorage
            localStorage.removeItem(resultKey);
        }
    }

    // Initialize when DOM is ready
    document.addEventListener('DOMContentLoaded', autoFillProductSKU);

})();